import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { Observable, throwError, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

export interface DealMetrics {
  internalRating: string;
  internalScore: number;
  internalStatus: string;
  ownership: boolean;
}

@Injectable()
export class DealMetricsService {
  private apiHost: string;

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfig
  ) {
    this.apiHost = environmentConfig.params.apiHost as string;
  }

  getDealMetrics(dealId: string): Observable<DealMetrics> {
    const url = `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/metrics`;
    return this.http
      .get<DealMetrics>(url, { observe: 'response' })
      .pipe(
        mergeMap((res: HttpResponse<DealMetrics>) => {
          const data = res.status === 200 && res.body;
          return data ? of(data) : throwError('Error getting deal metrics');
        })
      );
  }

  saveStatus(dealId: string, status: string): Observable<unknown | Error> {
    const url = `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/metrics/internal-status`;

    const body = {
      internalStatus: status
    };

    return this.http
      .put(url, body, {
        observe: 'response'
      })
      .pipe(
        mergeMap((response: HttpResponse<any>) => {
          return response.status === 200
            ? of(status)
            : throwError('Cannot save internal status');
        })
      );
  }

  saveRating(dealId: string, rating: string): Observable<unknown | Error> {
    const url = `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/metrics/internal-rating`;

    const body = {
      internalRating: rating
    };

    return this.http
      .put(url, body, {
        observe: 'response'
      })
      .pipe(
        mergeMap((response: HttpResponse<any>) => {
          return response.status === 200
            ? of(rating)
            : throwError('Cannot save internal rating');
        })
      );
  }

  saveScore(dealId: string, score: string): Observable<unknown | Error> {
    const url = `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/metrics/internal-score`;

    const body = {
      internalScore: score
    };

    return this.http
      .put(url, body, {
        observe: 'response'
      })
      .pipe(
        mergeMap((response: HttpResponse<any>) => {
          return response.status === 200
            ? of(score)
            : throwError('Cannot save internal score');
        })
      );
  }

  saveOwnership(
    dealId: string,
    ownership: boolean
  ): Observable<unknown | Error> {
    const url = `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/metrics/ownership`;

    const body = {
      ownership: ownership
    };

    return this.http
      .put(url, body, {
        observe: 'response'
      })
      .pipe(
        mergeMap((response: HttpResponse<any>) => {
          return response.status === 200
            ? of(ownership)
            : throwError('Cannot save ownership');
        })
      );
  }
}
